import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { gestorVisivel } from '../lib/filtroGestor'
import { abrirAnexo, rotuloAnexo, temAnexo } from '../lib/anexos'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
const ANO_ATUAL = new Date().getFullYear()

function ThumbIcon({ up, size = 16, color, label }) {
  const path = up
    ? 'M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z'
    : 'M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z'
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }} aria-hidden="true">
      {label && <title>{label}</title>}
      <path d={path} />
    </svg>
  )
}

function StatusThumb({ status, size }) {
  if (status === true) return <ThumbIcon up size={size} color="var(--green)" label="Meta batida no mês anterior" />
  if (status === false) return <ThumbIcon up={false} size={size} color="var(--red)" label="Meta não batida no mês anterior" />
  return null
}

function IndicadorSelect({ options, value, onChange, statusMap, showResp, fs, minWidth }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const selected = options.find(o => o.id === value)
  const fontSize = fs ? 14 : 13
  return (
    <div ref={ref} style={{ position: 'relative', minWidth }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '8px 12px', fontSize, textAlign: 'left', fontFamily: 'var(--font)',
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          color: selected ? 'var(--text)' : 'var(--text3)', cursor: 'pointer'
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.nome + (showResp && selected.profiles ? ` — ${selected.profiles.nome}` : '') : 'Selecione um indicador...'}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {selected && <StatusThumb status={statusMap[selected.id] ?? null} size={fs ? 16 : 14} />}
          <span style={{ color: 'var(--text3)', fontSize: 10 }}>▾</span>
        </span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--shadow-md)', maxHeight: 340, overflowY: 'auto', padding: 4
        }}>
          {options.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize, color: 'var(--text3)' }}>Nenhum indicador</div>
          )}
          {options.map(o => {
            const isSel = o.id === value
            return (
              <div key={o.id} onClick={() => { onChange(o.id); setOpen(false) }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  padding: '7px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize,
                  background: isSel ? 'var(--blue-bg)' : 'transparent',
                  color: isSel ? 'var(--blue)' : 'var(--text)'
                }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--surface2)' }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.nome}{showResp && o.profiles ? ` — ${o.profiles.nome}` : ''}
                </span>
                <StatusThumb status={statusMap[o.id] ?? null} size={fs ? 16 : 15} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function Dashboard() {
  const { id } = useParams()
  const { isAdmin, profile, isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const navigate = useNavigate()
  const [indicadores, setIndicadores] = useState([])
  const [indSel, setIndSel] = useState(id ? Number(id) : null)
  const [ind, setInd] = useState(null)
  const [resultados, setResultados] = useState([])
  const [fcas, setFcas] = useState([])
  const [ano, setAno] = useState(ANO_ATUAL)
  const [loading, setLoading] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [erroAnexo, setErroAnexo] = useState(null)
  const [fcaOverlay, setFcaOverlay] = useState(null) // { mes, idx }
  const [statusMap, setStatusMap] = useState({}) // indicador_id -> true/false (meta batida no mês anterior)
  // Mesma preferencia usada na tela de Indicadores
  const [mostrarTaticos, setMostrarTaticos] = useState(
    () => localStorage.getItem('mostrarTaticos') === 'true'
  )
  const containerRef = useRef(null)

  useEffect(() => { localStorage.setItem('mostrarTaticos', String(mostrarTaticos)) }, [mostrarTaticos])

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement)
      if (!document.fullscreenElement) setFcaOverlay(null)
    }
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  function toggleFullscreen() {
    if (!document.fullscreenElement) containerRef.current?.requestFullscreen()
    else document.exitFullscreen()
  }

  useEffect(() => {
    let q = supabase.from('indicadores').select('id,nome,objetivo,gestor_id,apenas_tatico,profiles(id,nome)').order('nome')
    if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)
    q.then(({ data }) => { setIndicadores(data || []); setLoading(false) })
  }, [selectedCompany])

  // Status do mês anterior por indicador (para o polegar no seletor)
  useEffect(() => {
    if (indicadores.length === 0) { setStatusMap({}); return }
    const now = new Date()
    const mesAnt = now.getMonth() === 0 ? 12 : now.getMonth()
    const anoAnt = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
    const objMap = Object.fromEntries(indicadores.map(i => [i.id, i.objetivo]))
    supabase.from('resultados')
      .select('indicador_id, meta, resultado')
      .eq('ano', anoAnt).eq('mes', mesAnt)
      .in('indicador_id', indicadores.map(i => i.id))
      .then(({ data }) => {
        const m = {}
        for (const r of (data || [])) {
          if (r.resultado == null || r.meta == null) continue
          m[r.indicador_id] = objMap[r.indicador_id] === 'maior'
            ? r.resultado >= r.meta
            : r.resultado <= r.meta
        }
        setStatusMap(m)
      })
  }, [indicadores])

  useEffect(() => {
    if (!indSel) return
    fetchData()
  }, [indSel, ano])

  async function fetchData() {
    const [{ data: ind }, { data: res }, { data: fca }] = await Promise.all([
      supabase.from('indicadores').select('*, profiles(nome,cargo)').eq('id', indSel).single(),
      supabase.from('resultados').select('*').eq('indicador_id', indSel).eq('ano', ano).order('mes'),
      supabase.from('fcas').select('*').eq('indicador_id', indSel).eq('ano', ano)
    ])
    setInd(ind)
    setResultados(res || [])
    setFcas(fca || [])
  }

  const chartData = MESES.map((m, i) => {
    const r = resultados.find(x => x.mes === i + 1)
    return {
      mes: m,
      meta: r?.meta ?? null,
      resultado: r?.resultado ?? null,
      hasData: r != null && r.resultado != null,
      fca: fcas.find(f => f.mes === i + 1) ? '*' : null
    }
  })

  const acumulado = (() => {
    if (!ind) return chartData.map(d => ({ ...d, acumulado: null }))
    if (ind.tipo_acumulado === 'atualizavel') {
      return chartData.map((d, i) => {
        const r = resultados.find(x => x.mes === i + 1)
        return { ...d, acumulado: r?.acumulado ?? null }
      })
    }
    let sum = 0, count = 0
    return chartData.map(d => {
      if (!d.hasData) return { ...d, acumulado: null }
      sum += d.resultado; count++
      const val = ind.tipo_acumulado === 'soma' ? sum : sum / count
      return { ...d, acumulado: parseFloat(val.toFixed(2)) }
    })
  })()

  const yDomain = (() => {
    const vals = resultados.flatMap(r => [r.resultado, r.meta]).filter(v => v != null)
    if (vals.length === 0) return ['auto', 'auto']
    const min = Math.min(...vals)
    return [parseFloat(Math.max(0, min * 0.9).toFixed(2)), 'auto']
  })()

  const gestores = [...new Map(
    indicadores.filter(i => i.profiles).map(i => [i.profiles.id, i.profiles])
  ).values()].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))

  const qtdTaticos = indicadores.filter(i => i.apenas_tatico).length

  // Filtro de gestor compartilhado com as outras telas (ver AuthContext).
  // So' vale para quem tem o seletor: sem ele na tela, a pessoa nao teria como
  // perceber nem desfazer o recorte.
  const podeFiltrarGestor = isAdmin || isSuperAdmin
  const gestorSel = podeFiltrarGestor ? gestorVisivel(filtroGestor, gestores) : ''

  const indicadoresFiltrados = (gestorSel
    // Pelo mesmo motivo dos taticos abaixo, o indicador aberto continua na
    // lista mesmo sendo de outro responsavel — um link /dashboard/:id vindo
    // de outra tela nao pode cair num seletor vazio.
    ? indicadores.filter(i => i.profiles?.id === gestorSel || String(i.id) === String(indSel))
    : indicadores
  ).filter(i =>
    // Esconde taticos, mas nunca o que ja' esta' selecionado — senao um
    // link direto /dashboard/:id de um tatico abriria com o seletor vazio.
    mostrarTaticos || !i.apenas_tatico || String(i.id) === String(indSel)
  )

  const comDados = resultados.filter(r => r.resultado !== null && r.meta !== null)
  const atingidos = comDados.filter(r =>
    ind ? (ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta) : false
  ).length

  const fs = isFullscreen

  function renderFcaOverlay() {
    if (!fcaOverlay) return null
    const fcasMes = fcas.filter(f => f.mes === fcaOverlay.mes).sort((a, b) => a.id - b.id)
    const fca = fcasMes[fcaOverlay.idx]
    if (!fca) return null
    const res = resultados.find(r => r.mes === fcaOverlay.mes)
    const total = fcasMes.length
    const idx = fcaOverlay.idx

    const porques = [1,2,3,4,5].map(n => fca[`porque_${n}`]).filter(Boolean)

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'var(--bg)', zIndex: 9999, overflowY: 'auto',
        padding: '28px 40px', boxSizing: 'border-box'
      }}>
        {/* Barra superior */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 22 }}>FCA — {MESES[fcaOverlay.mes - 1]}/{ano}</span>
            {total > 1 && (
              <span style={{
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 20, padding: '2px 10px', fontSize: 13, color: 'var(--text2)'
              }}>{idx + 1} / {total}</span>
            )}
            <span className={`badge ${fca.status === 'Concluído' ? 'badge-green' : fca.status === 'Em andamento' ? 'badge-amber' : 'badge-red'}`}>
              {fca.status}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {total > 1 && (
              <>
                <button className="btn" onClick={() => setFcaOverlay(o => ({ ...o, idx: o.idx - 1 }))} disabled={idx === 0}>
                  ‹ Anterior
                </button>
                <button className="btn" onClick={() => setFcaOverlay(o => ({ ...o, idx: o.idx + 1 }))} disabled={idx === total - 1}>
                  Próximo ›
                </button>
              </>
            )}
            <button className="btn" onClick={() => setFcaOverlay(null)}>✕ Fechar</button>
          </div>
        </div>

        {/* Indicador */}
        <div className="card" style={{ marginBottom: 14, padding: '14px 20px' }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{ind.nome}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            Gestor: <strong>{ind.profiles?.nome}</strong>
            {ind.profiles?.cargo ? ` · ${ind.profiles.cargo}` : ''}
          </div>
        </div>

        {/* Fato */}
        {fca.fato && (
          <div className="card" style={{ marginBottom: 14, padding: '14px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 1, marginBottom: 10 }}>FATO</div>
            <div style={{ fontSize: 15, lineHeight: 1.7 }}>{fca.fato}</div>
          </div>
        )}

        {/* Causa — 5 porquês */}
        {porques.length > 0 && (
          <div className="card" style={{ marginBottom: 14, padding: '14px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 1, marginBottom: 12 }}>CAUSA — 5 PORQUÊS</div>
            {porques.map((p, n) => (
              <div key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: n < porques.length - 1 ? 10 : 0 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: '#fffbeb', border: '1px solid #fde68a',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 600, color: '#92400e'
                }}>{n + 1}</div>
                <div style={{ fontSize: 14, lineHeight: 1.6, paddingTop: 3 }}>{p}</div>
              </div>
            ))}
          </div>
        )}

        {/* Plano de ação */}
        {(fca.acao || fca.como || fca.por_que_fazer || fca.onde || fca.responsavel || fca.quando || fca.custo) && (
          <div className="card" style={{ marginBottom: 14, padding: '14px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 1, marginBottom: 14 }}>PLANO DE AÇÃO</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {fca.acao && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>O QUE SERÁ FEITO</div>
                  <div style={{ fontSize: 14, lineHeight: 1.6 }}>{fca.acao}</div>
                </div>
              )}
              {fca.como && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>COMO FAZER</div>
                  <div style={{ fontSize: 14, lineHeight: 1.6 }}>{fca.como}</div>
                </div>
              )}
              {fca.por_que_fazer && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>POR QUE FAZER</div>
                  <div style={{ fontSize: 14, lineHeight: 1.6 }}>{fca.por_que_fazer}</div>
                </div>
              )}
              {fca.onde && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>ONDE</div>
                  <div style={{ fontSize: 14 }}>{fca.onde}</div>
                </div>
              )}
            </div>
            {(fca.responsavel || fca.quando || fca.custo || fca.agenda_critica) && (
              <div style={{ display: 'flex', gap: 24, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
                {fca.responsavel && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>RESPONSÁVEL</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{fca.responsavel}</div>
                  </div>
                )}
                {fca.quando && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>PRAZO</div>
                    <div style={{ fontSize: 14, fontFamily: 'var(--mono)' }}>{fca.quando}</div>
                  </div>
                )}
                {fca.custo && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>CUSTO</div>
                    <div style={{ fontSize: 14 }}>{fca.custo}</div>
                  </div>
                )}
                {fca.agenda_critica && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>ANÁLISE CRÍTICA</div>
                    <div style={{ fontSize: 14, fontFamily: 'var(--mono)' }}>{fca.agenda_critica}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Resultado obtido / Conclusão */}
        {(fca.resultado_acao || fca.conclusao) && (
          <div className="card" style={{ marginBottom: 14, padding: '14px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 1, marginBottom: 10 }}>RESULTADO OBTIDO</div>
            {fca.resultado_acao && (
              <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: fca.conclusao ? 14 : 0 }}>{fca.resultado_acao}</div>
            )}
            {fca.conclusao && (
              <span style={{
                display: 'inline-block', padding: '4px 16px', borderRadius: 20,
                fontSize: 13, fontWeight: 600,
                background: fca.conclusao === 'Efetivo' ? '#f0fdf4' : '#fef2f2',
                color: fca.conclusao === 'Efetivo' ? 'var(--green)' : 'var(--red)',
                border: `1px solid ${fca.conclusao === 'Efetivo' ? '#bbf7d0' : '#fecaca'}`
              }}>
                {fca.conclusao}
              </span>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Controles — sempre fora do fullscreen */}
      {!fs && (
        <div className="page-title">
          <h2>Dashboard</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {podeFiltrarGestor && gestores.length > 0 && (
              <select value={gestorSel} onChange={e => { setFiltroGestor(e.target.value); setIndSel(null) }} style={{ width: 'auto', minWidth: 160 }}>
                <option value="">Todos os gestores</option>
                {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            )}
            <IndicadorSelect
              options={indicadoresFiltrados}
              value={indSel}
              onChange={id => { setIndSel(id); navigate(`/dashboard/${id}`) }}
              statusMap={statusMap}
              showResp={isAdmin}
              fs={false}
              minWidth={200}
            />
            <select value={ano} onChange={e => setAno(Number(e.target.value))} style={{ width: 'auto' }}>
              {[ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1].map(a => <option key={a}>{a}</option>)}
            </select>
            {qtdTaticos > 0 && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 6, margin: 0,
                fontSize: 12, color: 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap'
              }}>
                <input type="checkbox" checked={mostrarTaticos}
                  onChange={e => setMostrarTaticos(e.target.checked)}
                  style={{ width: 'auto', margin: 0, cursor: 'pointer' }} />
                Exibir táticos ({qtdTaticos})
              </label>
            )}
            {indSel && (
              <button className="btn" onClick={toggleFullscreen} title="Tela cheia" style={{ padding: '0 12px', fontSize: 16 }}>⛶</button>
            )}
          </div>
        </div>
      )}

      {!indSel && !fs ? (
        <div className="empty-state">Selecione um indicador para visualizar o gráfico.</div>
      ) : loading || !ind ? (
        <div className="empty-state">Carregando...</div>
      ) : (
        <div
          ref={containerRef}
          style={fs ? {
            background: 'var(--bg)', overflowY: 'auto', height: '95vh',
            padding: '24px 32px', boxSizing: 'border-box'
          } : {}}
        >
          {/* Overlay FCA — renderizado dentro do elemento fullscreen */}
          {fs && renderFcaOverlay()}

          {/* Botão sair tela cheia */}
          {fs && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {podeFiltrarGestor && gestores.length > 0 && (
                  <select value={gestorSel} onChange={e => { setFiltroGestor(e.target.value); setIndSel(null) }} style={{ width: 'auto', minWidth: 160, fontSize: 14 }}>
                    <option value="">Todos os gestores</option>
                    {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                  </select>
                )}
                <IndicadorSelect
                  options={indicadoresFiltrados}
                  value={indSel}
                  onChange={id => { setIndSel(id); navigate(`/dashboard/${id}`) }}
                  statusMap={statusMap}
                  showResp={isAdmin}
                  fs={true}
                  minWidth={260}
                />
                <select value={ano} onChange={e => setAno(Number(e.target.value))} style={{ width: 'auto', fontSize: 14 }}>
                  {[ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1].map(a => <option key={a}>{a}</option>)}
                </select>
                {qtdTaticos > 0 && (
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 6, margin: 0,
                    fontSize: 14, color: 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap'
                  }}>
                    <input type="checkbox" checked={mostrarTaticos}
                      onChange={e => setMostrarTaticos(e.target.checked)}
                      style={{ width: 'auto', margin: 0, cursor: 'pointer' }} />
                    Exibir táticos ({qtdTaticos})
                  </label>
                )}
              </div>
              <button className="btn" onClick={toggleFullscreen} style={{ fontSize: 13 }}>✕ Sair da tela cheia</button>
            </div>
          )}

          {/* Info header */}
          <div className="card" style={{ marginBottom: 10, padding: fs ? '16px 24px' : '12px 16px' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: fs ? 22 : 15, marginBottom: 4 }}>{ind.nome}</div>
                <div style={{ fontSize: fs ? 14 : 12, color: 'var(--text2)' }}>
                  Responsável: <strong>{ind.profiles?.nome}</strong> · {ind.profiles?.cargo} ·
                  Unidade: <strong>{ind.unidade || '—'}</strong> ·
                  Objetivo: <strong>{ind.objetivo === 'maior' ? 'maior é melhor' : 'menor é melhor'}</strong> ·
                  Acumulado: <strong>{{ soma: 'Soma', media: 'Média', atualizavel: 'Atualizável' }[ind.tipo_acumulado] || 'Soma'}</strong>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { label: 'Meses c/ dados', val: comDados.length, color: 'var(--text)' },
                  { label: 'Metas atingidas', val: `${atingidos}/${comDados.length}`, color: atingidos === comDados.length && comDados.length > 0 ? 'var(--green)' : 'var(--red)' },
                  { label: 'FCAs abertos', val: fcas.filter(f => f.status !== 'Concluído').length, color: fcas.length > 0 ? 'var(--amber)' : 'var(--green)' },
                ].map(m => (
                  <div key={m.label} style={{
                    background: 'var(--surface2)', borderRadius: 8,
                    padding: fs ? '10px 24px' : '6px 14px', textAlign: 'center', minWidth: fs ? 110 : 80
                  }}>
                    <div style={{ fontSize: fs ? 12 : 10, color: 'var(--text2)', marginBottom: 2 }}>{m.label}</div>
                    <div style={{ fontSize: fs ? 28 : 18, fontWeight: 700, color: m.color }}>{m.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Gráfico */}
          <div className="card" style={{ marginBottom: 10, padding: fs ? '16px 24px' : '10px 16px' }}>
            <ResponsiveContainer width="100%" height={fs ? Math.max(200, Math.round(window.innerHeight * 0.35)) : 224}>
              <ComposedChart data={acumulado} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e0d8" />
                <XAxis dataKey="mes" tick={{ fontSize: fs ? 14 : 11, fill: '#6b6960' }} />
                <YAxis domain={yDomain} tick={{ fontSize: fs ? 14 : 11, fill: '#6b6960' }} />
                <Tooltip
                  contentStyle={{ border: '1px solid #e2e0d8', borderRadius: 8, fontSize: fs ? 15 : 13 }}
                  formatter={(v, name) => [v !== null ? v.toFixed(2) : '—', name]}
                />
                <Legend wrapperStyle={{ fontSize: fs ? 14 : 11, paddingTop: 4 }} />
                <Bar dataKey="resultado" name="Resultado mês" fill="#378ADD" radius={[3, 3, 0, 0]} />
                {ind.tipo_acumulado !== 'soma' && (
                  <Line dataKey="acumulado" name={{ media: 'Acumulado (média)', atualizavel: 'Acumulado' }[ind.tipo_acumulado] || 'Acumulado'} stroke="#EF9F27" strokeWidth={fs ? 3 : 2} dot={{ r: fs ? 5 : 3 }} connectNulls />
                )}
                <Line dataKey="meta" name="Meta mês" stroke="#185FA5" strokeWidth={fs ? 2.5 : 1.5} strokeDasharray="5 4" dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Tabela horizontal */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: fs ? '14px 24px' : '10px 16px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: fs ? 16 : undefined }}>Resultado mensal — {ano}</h3>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 100 }}></th>
                    {MESES.map(m => <th key={m} style={{ textAlign: 'center', minWidth: 70 }}>{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {/* Meta */}
                  <tr>
                    <td style={{ fontWeight: 500, color: 'var(--text2)', fontSize: 12 }}>Meta</td>
                    {acumulado.map((row, i) => {
                      const r = resultados.find(x => x.mes === i + 1)
                      return (
                        <td key={i} style={{ fontFamily: 'var(--mono)', fontSize: 13, textAlign: 'center' }}>
                          {r?.meta != null ? Number(r.meta).toFixed(ind.casas_decimais ?? 0) : '—'}
                        </td>
                      )
                    })}
                  </tr>
                  {/* Resultado */}
                  <tr>
                    <td style={{ fontWeight: 500, color: 'var(--text2)', fontSize: 12 }}>Resultado</td>
                    {acumulado.map((row, i) => {
                      const r = resultados.find(x => x.mes === i + 1)
                      const ok = r?.resultado != null && r?.meta != null
                        ? (ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta)
                        : null
                      return (
                        <td key={i} style={{
                          fontFamily: 'var(--mono)', fontSize: 13, textAlign: 'center',
                          color: ok === true ? 'var(--green)' : ok === false ? 'var(--red)' : undefined,
                          fontWeight: ok !== null ? 600 : undefined,
                        }}>
                          {r?.resultado != null ? Number(r.resultado).toFixed(ind.casas_decimais ?? 0) : '—'}
                        </td>
                      )
                    })}
                  </tr>
                  {/* Acumulado */}
                  <tr>
                    <td style={{ fontWeight: 500, color: 'var(--text2)', fontSize: 12 }}>Acumulado</td>
                    {acumulado.map((row, i) => (
                      <td key={i} style={{ fontFamily: 'var(--mono)', fontSize: 13, textAlign: 'center' }}>
                        {row.acumulado !== null ? Number(row.acumulado).toFixed(ind.casas_decimais ?? 0) : '—'}
                      </td>
                    ))}
                  </tr>
                  {/* Status */}
                  <tr>
                    <td style={{ fontWeight: 500, color: 'var(--text2)', fontSize: 12 }}>Status</td>
                    {acumulado.map((row, i) => {
                      const r = resultados.find(x => x.mes === i + 1)
                      const ok = r?.resultado != null && r?.meta != null
                        ? (ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta)
                        : null
                      return (
                        <td key={i} style={{ textAlign: 'center' }}>
                          {ok === null
                            ? <span className="badge badge-gray">—</span>
                            : ok
                              ? <span className="badge badge-green" style={{ minWidth: 48 }}>OK</span>
                              : <span className="badge badge-red" style={{ minWidth: 48 }}>NOK</span>}
                        </td>
                      )
                    })}
                  </tr>
                  {/* FCA */}
                  <tr>
                    <td style={{ fontWeight: 500, color: 'var(--text2)', fontSize: 12 }}>FCA</td>
                    {acumulado.map((row, i) => {
                      const mes = i + 1
                      const fcasMes = fcas.filter(f => f.mes === mes).sort((a, b) => a.id - b.id)
                      const temFca = fcasMes.length > 0
                      const label = fcasMes.length > 1 ? `${fcasMes.length} FCAs` : fcasMes[0]?.status
                      return (
                        <td key={i} style={{ textAlign: 'center' }}>
                          {temFca ? (
                            <button
                              style={{ borderRadius: 20, border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}
                              onClick={() => fs ? setFcaOverlay({ mes, idx: 0 }) : navigate('/fcas')}>
                              {label}
                            </button>
                          ) : '—'}
                        </td>
                      )
                    })}
                  </tr>
                  {/* Planilha de estratificação — só aparece se algum mês tiver */}
                  {resultados.some(temAnexo) && (
                    <tr>
                      <td style={{ fontWeight: 500, color: 'var(--text2)', fontSize: 12 }}>Planilha</td>
                      {acumulado.map((row, i) => {
                        const r = resultados.find(x => x.mes === i + 1)
                        return (
                          <td key={i} style={{ textAlign: 'center' }}>
                            {temAnexo(r) ? (
                              <button
                                title={rotuloAnexo(r)}
                                style={{ borderRadius: 20, border: '1px solid var(--blue-border)', background: 'var(--blue-bg)', color: '#1e40af', fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}
                                onClick={async () => {
                                  const { error } = await abrirAnexo(r)
                                  if (error) setErroAnexo(error)
                                }}>
                                {r.anexo_tipo === 'link' ? '🔗' : '📄'}
                              </button>
                            ) : '—'}
                          </td>
                        )
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
              {erroAnexo && (
                <div style={{ fontSize: 12, color: 'var(--red)', padding: '8px 0 0' }}>⚠ {erroAnexo}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
